import React, { useState, useEffect } from 'react';
import { X, Save, User, Mail, Phone, MapPin, Building, FileText } from 'lucide-react';

const ContactEditModal = ({ contact, isOpen, onClose, onUpdate, availableTags = [], getTagColor }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    venue: '',
    city: '',
    notes: '',
    tags: []
  });

  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        venue: contact.venue || '',
        city: contact.city || '',
        notes: contact.notes || '',
        tags: contact.tags || []
      });
    }
  }, [contact]);

  if (!isOpen || !contact) return null;

  const handleSubmit = () => {
    if (!formData.name.trim()) return;
    onUpdate(contact.id, formData);
  };

  const toggleTag = (tagValue) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.includes(tagValue)
        ? prev.tags.filter(t => t !== tagValue)
        : [...prev.tags, tagValue]
    }));
  };

  const InputField = ({ icon: Icon, label, type = 'text', field, placeholder }) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
        {label}
      </label>
      <div className="relative">
        <Icon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type={type}
          value={formData[field]}
          onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
          placeholder={placeholder}
          className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:border-blue-500 text-sm transition-colors"
        />
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="modal-enter bg-white dark:bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Edit Contact</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {contact.fromShow ? 'This contact is linked to a show' : 'Update contact information'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="btn-press p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-180px)] space-y-4">
          <InputField icon={User} label="Name *" field="name" placeholder="John Doe" />
          <InputField icon={Mail} label="Email" type="email" field="email" placeholder="john@example.com" />
          <InputField icon={Phone} label="Phone" type="tel" field="phone" placeholder="(555) 123-4567" />
          <InputField icon={Building} label="Venue / Company" field="venue" placeholder="The Blue Note" />
          <InputField icon={MapPin} label="City" field="city" placeholder="New York, NY" />
          <InputField icon={FileText} label="Notes" field="notes" placeholder="Additional notes..." />

          {/* Tags */}
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">Tags</label>
            <div className="flex flex-wrap gap-2">
              {availableTags.map(tag => {
                const isSelected = formData.tags.includes(tag.value);
                return (
                  <button
                    key={tag.value}
                    type="button"
                    onClick={() => toggleTag(tag.value)}
                    className={`btn-press px-3 py-1.5 rounded-full text-sm font-semibold transition-all ${
                      isSelected
                        ? tag.color + ' ring-2 ring-blue-500'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    {tag.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Show source indicator */}
          {contact.fromShow && (
            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-700 dark:text-blue-400">
              <strong>Note:</strong> Changes will update the contact info on the linked show.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="btn-press px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!formData.name.trim()}
            className={`btn-press px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-colors ${
              formData.name.trim()
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
            }`}
          >
            <Save size={16} />
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactEditModal;